import 'mapbox-gl/dist/mapbox-gl.css'
import Map, { Layer, Marker, ScaleControl, Source } from 'react-map-gl'
import type { ProgressMapData } from '../../hooks/map/useProgressMapData'
import MapLegend, { PaceSetterMarker, PlayerMarker } from './MapLegend'
import styles from './MapScreen.module.css'

export interface ProgressMapProps {
  interactive?: boolean
  completedColor: string
  remainingColor: string
  data: ProgressMapData
}

export default function ProgressMap({
  interactive = false,
  completedColor,
  remainingColor,
  data,
}: ProgressMapProps) {
  const {
    yourProgress,
    leaderProgress,
    yourPoint,
    leaderPoint,
    pelotonPoint,
    yourRoute,
    remainingRoute,
    bounds,
  } = data

  const layerStyles = {
    completed: {
      id: 'line-layer-completed',
      type: 'line' as const,
      paint: {
        'line-color': completedColor,
        'line-width': 4,
      },
    },
    remaining: {
      id: 'line-layer-remaining',
      type: 'line' as const,
      paint: {
        'line-color': remainingColor,
        'line-width': 2,
        'line-dasharray': [2, 2],
      },
    },
  }

  return (
    <div className={styles.mapRoot}>
      <Map
        initialViewState={{
          bounds,
          fitBoundsOptions: { padding: 50 },
        }}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        mapStyle="mapbox://styles/mapbox/dark-v11"
        style={{ width: '100%', height: '100%' }}
        interactive={interactive}
        attributionControl={false}
      >
        {yourRoute.geometry.coordinates.length >= 2 && (
          <Source id="completedRoute" type="geojson" data={yourRoute}>
            <Layer {...layerStyles.completed} />
          </Source>
        )}

        {remainingRoute.geometry.coordinates.length >= 2 && (
          <Source id="remainingRoute" type="geojson" data={remainingRoute}>
            <Layer {...layerStyles.remaining} />
          </Source>
        )}

        <Marker
          latitude={leaderPoint.geometry.coordinates[1]!}
          longitude={leaderPoint.geometry.coordinates[0]!}
          anchor="bottom"
        >
          <PlayerMarker isLeader />
        </Marker>

        <Marker
          latitude={pelotonPoint.geometry.coordinates[1]!}
          longitude={pelotonPoint.geometry.coordinates[0]!}
          anchor="bottom"
        >
          <PaceSetterMarker />
        </Marker>

        <Marker
          latitude={yourPoint.geometry.coordinates[1]!}
          longitude={yourPoint.geometry.coordinates[0]!}
          anchor="bottom"
        >
          <PlayerMarker isLeader={yourProgress >= leaderProgress} />
        </Marker>

        <ScaleControl position="top-right" />
      </Map>
      <MapLegend yourProgress={yourProgress} leaderProgress={leaderProgress} />
    </div>
  )
}
